import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Item Scoring|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamsItemScoringImport';
export type SPCatalogsExamsItemScoringImportLocNames =
	| `${LocNameStart}Title`
	| `${LocNameStart}ButtonHideInstructions`
	| `${LocNameStart}ButtonShowInstructions`
	| `${LocNameStart}ButtonDownloadTemplate`
	| `${LocNameStart}ButtonImport`
	| `${LocNameStart}ButtonUpsert`
	| `${LocNameStart}ContentImportInstructions`;

const instructions = `
	<p>
		To import new items, click the DOWNLOAD IMPORT TEMPLATE button, fill in what items you would
		like, and click IMPORT ITEMS. See <strong>Import Instructions </strong> below for details.
	</p>
	<p>
		To Update new items, download the data from the item table below using the download button,
		modify the items you wish, and then click UPDATE ITEMS. For Detailed Instructions, see
		<strong>Update Instructions</strong> below.
	</p>

	<h3>Import Instructions</h3>
	<p>
		The import button uses a comma separated value file (CSV). A sample file can be obtained by
		clicking the DOWNLOAD IMPORT TEMPLATE button above. This file needs to contain the following
		fields:
	</p>
	<ul>
		<li>Name (required for import)</li>
		<li>Language (defaults to English)</li>
		<li>Weight</li>
		<li>Maximum</li>
		<li>Comment</li>
		<li>Unscored Forms</li>
	</ul>
	<p>
		The "Name" column data must match an item name in the system. Item names are part of the data
		feed from a test data provider.
	</p>
	<p>The "Language" column is the 3 character Windows API language code.</p>
	<p>
		The "Weight" is the score weight of a given item. Unscored items are left at 0; scored items are
		normally given a "1".
	</p>
	<p>
		The "Maximum" is the maximum score possible for an item. In polytomous scoring, an item can be
		given a variable amount of points, depending on how the candidate answered. In dichotomous
		scoring, the score always translates to "correct" and "incorrect", and the maximum is presumed
		to be "1".
	</p>
	<p>A "Comment" stores an item definition for psychometric reporting.</p>
	<p>
		"Unscored Forms" gives a "|" (or pipe) delimited list of form names where this item is unscored.
		The form names will have leading and trailing white space removed.
	</p>
	<h3>Update Instructions</h3>
	<p>
		The update button uses a comma separated value file (CSV). A sample file can be obtained by
		downloading the item data below using the download button to the top right of the table. This
		file needs to contain the following fields:
	</p>
	<ul>
		<li>Name (required for update)</li>
		<li>Language (required for update)</li>
		<li>Weight</li>
		<li>Maximum</li>
		<li>Comment</li>
		<li>Unscored Forms</li>
	</ul>
	<p>
		If the "Name" and "Language" columns match an existing item, it will be updated. If they do not
		match, a new item will be created. This will allow you to create and update items in one file if
		you wish
	</p>
	<p>
		The "Weight" is the score weight of a given item. Unscored items are left at 0; scored items are
		normally given a "1".
	</p>
	<p>
		The "Maximum" is the maximum score possible for an item. In polytomous scoring, an item can be
		given a variable amount of points, depending on how the candidate answered. In dichotomous
		scoring, the score always translates to "correct" and "incorrect", and the Maximum is presumed
		to be "1".
	</p>
	<p>A "Comment" stores an item definition for psychometric reporting.</p>
	<p>
		"Unscored Forms" gives a "|" (or pipe) delimited list of form names where this item is unscored.
		The form names will have leading and trailing white space removed.
	</p>
	`;

export const spCatalogsExamsItemScoringImportLocs: LocDefs<SPCatalogsExamsItemScoringImportLocNames> =
	{
		SPCatalogsExamsItemScoringImportTitle: {
			defaultValue: 'Item Scoring Import And Update',
			description: 'Title',
			displayPath: `${pathStart}Title`,
			displayType: 'Title',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Title.Import',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringImportButtonHideInstructions: {
			defaultValue: 'Hide Instructions',
			description: 'Hide Instructions button.',
			displayPath: `${pathStart}Hide Instructions`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.HideInstructions',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringImportButtonShowInstructions: {
			defaultValue: 'Show Instructions',
			description: 'Show Instructions button.',
			displayPath: `${pathStart}Show Instructions`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.ShowInstructions',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringImportButtonDownloadTemplate: {
			defaultValue: 'Download Import Template',
			description: 'Download Import Template button.',
			displayPath: `${pathStart}Download Import Template`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.DownloadTemplate',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringImportButtonImport: {
			defaultValue: 'Import Items',
			description: 'Button to import New Items with CSV.',
			displayPath: `${pathStart}Import New Items with CSV`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.Import',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringImportButtonUpsert: {
			defaultValue: 'Update Items',
			description: 'Button to import and update Existing Items with CSV.',
			displayPath: `${pathStart}Import New Items with CSV`,
			displayType: 'Button',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Button.Import',
			...sharedProperties,
		},
		SPCatalogsExamsItemScoringImportContentImportInstructions: {
			defaultValue: instructions,
			description: 'Import instructions.',
			displayPath: `${pathStart}Import instructions`,
			displayType: 'Content',
			legacyName: 'Exam/Catalogs.Exams.ItemScoring.Content.ImportInstructions',
			...sharedProperties,
			htmlAllowed: true,
		},
	};

export type SPCatalogsExamsItemScoringImportLocType =
	LocMap<SPCatalogsExamsItemScoringImportLocNames>;
